import { pointInPolygon } from './shadow';

/**
 * A uniform grid over building shadow hulls, so classifying a canopy point
 * tests only the hulls that could plausibly contain it.
 *
 * Why this exists: classification used to run against the handful of buildings
 * the user had clicked, so a linear scan was free. Now every building in the
 * viewport participates, and at z17 that is ~269 buildings against ~589k canopy
 * points -- 158 million point-in-polygon tests per sun position, which is far
 * too slow to scrub interactively.
 *
 * Bucketing hulls by their bounding box brings that to roughly one or two
 * candidate tests per point. The grid is rebuilt whenever the sun moves (the
 * hulls themselves change shape), so construction has to stay cheap: it is
 * O(hulls x cells-covered), with no per-point allocation in the query path.
 */

/** ~40 m at NYC's latitude. Buildings are mostly smaller than one cell. */
const CELL_DEGREES = 0.0005;

export type Hull = Array<[number, number]>;

interface Bounds {
  minLon: number;
  minLat: number;
  maxLon: number;
  maxLat: number;
}

export class HullIndex {
  readonly hulls: Hull[];
  private readonly bounds: Bounds[];
  private readonly cells = new Map<number, number[]>();
  private readonly originLon: number;
  private readonly originLat: number;
  private readonly cols: number;

  constructor(hulls: Hull[]) {
    this.hulls = hulls;
    this.bounds = hulls.map(boundsOf);

    if (hulls.length === 0) {
      this.originLon = 0;
      this.originLat = 0;
      this.cols = 1;
      return;
    }

    let minLon = Infinity;
    let minLat = Infinity;
    let maxLon = -Infinity;
    let maxLat = -Infinity;
    for (const b of this.bounds) {
      if (b.minLon < minLon) minLon = b.minLon;
      if (b.minLat < minLat) minLat = b.minLat;
      if (b.maxLon > maxLon) maxLon = b.maxLon;
      if (b.maxLat > maxLat) maxLat = b.maxLat;
    }

    this.originLon = minLon;
    this.originLat = minLat;
    this.cols = Math.max(1, Math.floor((maxLon - minLon) / CELL_DEGREES) + 1);

    for (let i = 0; i < this.bounds.length; i++) {
      const b = this.bounds[i];
      const x0 = this.col(b.minLon);
      const x1 = this.col(b.maxLon);
      const y0 = this.row(b.minLat);
      const y1 = this.row(b.maxLat);
      for (let y = y0; y <= y1; y++) {
        for (let x = x0; x <= x1; x++) {
          const key = y * this.cols + x;
          const bucket = this.cells.get(key);
          if (bucket) bucket.push(i);
          else this.cells.set(key, [i]);
        }
      }
    }
  }

  get size(): number {
    return this.hulls.length;
  }

  private col(lon: number): number {
    return Math.floor((lon - this.originLon) / CELL_DEGREES);
  }

  private row(lat: number): number {
    return Math.floor((lat - this.originLat) / CELL_DEGREES);
  }

  /**
   * Index of the first hull containing this point, or -1.
   *
   * Returning the index rather than a boolean is what lets the caller scope
   * statistics to one focused building while still coloring every point
   * against the whole viewport.
   */
  hullAt(lon: number, lat: number): number {
    if (this.hulls.length === 0) return -1;

    const bucket = this.cells.get(this.row(lat) * this.cols + this.col(lon));
    if (bucket === undefined) return -1;

    for (const i of bucket) {
      const b = this.bounds[i];
      // The bbox reject is much cheaper than the ray cast and skips most
      // candidates that merely share a cell.
      if (lon < b.minLon || lon > b.maxLon || lat < b.minLat || lat > b.maxLat) continue;
      if (pointInPolygon([lon, lat], this.hulls[i])) return i;
    }
    return -1;
  }

  /**
   * Calls `visit` with the index of *every* hull containing this point.
   *
   * `hullAt` returns the first match, which is only sound when the answer is a
   * yes/no about a single hull. Both the same-hull test in canopy.ts and the
   * narrow phase in occlusion.ts have to consider each building on its own
   * terms, and with hundreds of overlapping shadows the first match is rarely
   * the one that matters.
   *
   * A callback rather than an array because this runs once or twice per canopy
   * point -- half a million times per sun position -- and the query path has no
   * allocation in it today.
   */
  hullsAt(lon: number, lat: number, visit: (index: number) => void): void {
    if (this.hulls.length === 0) return;

    const bucket = this.cells.get(this.row(lat) * this.cols + this.col(lon));
    if (bucket === undefined) return;

    for (const i of bucket) {
      const b = this.bounds[i];
      if (lon < b.minLon || lon > b.maxLon || lat < b.minLat || lat > b.maxLat) continue;
      if (pointInPolygon([lon, lat], this.hulls[i])) visit(i);
    }
  }
}

function boundsOf(hull: Hull): Bounds {
  let minLon = Infinity;
  let minLat = Infinity;
  let maxLon = -Infinity;
  let maxLat = -Infinity;
  for (const [lon, lat] of hull) {
    if (lon < minLon) minLon = lon;
    if (lon > maxLon) maxLon = lon;
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
  }
  return { minLon, minLat, maxLon, maxLat };
}
